import { CircularProgress, Paper } from '@mui/material'
import { useMemo, useState } from 'react'
import FlowApiClient from '../../../backend/sub_api_clients/FlowApiClient'
import { IApprovalRequest } from '../../../definitions/Approval'
import { IFlow, INode } from '../../../definitions/Nodes'
import { IExecutionEnvironment } from '../../../definitions/NodesExecution'
import useQuery, { buildQueryKey } from '../../hooks/useQuery'
import NodeDataEditor from '../NodeDataEditor'
import AuditAccordion from './AuditAccordion'
import PanelSection from './PanelSection'

interface P {
    flow: IFlow
    selectedNode: INode
    isLoading: boolean
    nodeErrors: string[]
    execEnv: IExecutionEnvironment
    onClose: () => void
    approval: IApprovalRequest
}

const NodeAuditPanel = ({ flow, selectedNode, nodeErrors, isLoading, execEnv, onClose, approval }: P) => {
    if (!selectedNode || !execEnv) {
        return <div>Missing selected node or execution environment</div>
    }

    const [showModeInputs, setShowModeInputs] = useState('table')
    const [showModeOutputs, setShowModeOutputs] = useState('table')

    const { status: statusAuditData, response: auditData } = useQuery({
        queryKey: buildQueryKey('node-audit-data', selectedNode?.uuid, execEnv?.uuid, execEnv?.status),
        queryFn: async () => await FlowApiClient.getNodeAuditData(selectedNode?.uuid, execEnv?.uuid),
        cache: true
    })

    const filteredInputs = useMemo(() => {
        if (!auditData?.inputs) return []
        return auditData.inputs.filter(
            input =>
                selectedNode.inputs.find(x => x.outputKey === input.key)
                || input.key === 'exportData'
        )
    }, [auditData?.inputs, selectedNode.inputs])

    const outputs = useMemo(
        () => (auditData?.outputs ?? []).filter(output => output.value?.memoryData?.data !== null),
        [auditData?.outputs]
    )

    const outputError = auditData?.outputs?.length === 0 && auditData?.configs?.memoryData?.memory?.status === 'failed'

    return (
        <>
            <Paper
                sx={{
                    height: '85%',
                    minWidth: '25%',
                    maxWidth: '25%',
                    borderRadius: '12px 0px 0px 12px',
                    borderRight: 'none',
                    padding: 2
                }}>
                {statusAuditData === 'success'
                    ? (
                            <PanelSection
                                title='Inputs'
                                mode={showModeInputs}
                                setMode={setShowModeInputs}
                                data={filteredInputs}
                                renderContent={(input, i) => (
                                    <AuditAccordion key={i + 1} data={input} showMode={showModeInputs} nodeId={selectedNode.uuid} eeId={execEnv.uuid} inOut='input' />
                                )}
                            />
                        )
                    : <CircularProgress />}
            </Paper>
            <div style={{ height: '100%', width: '40%' }}>
                <NodeDataEditor
                    flow={flow}
                    isLoading={isLoading === false}
                    nodeErrors={nodeErrors}
                    defaultSelected={selectedNode}
                    mode='read'
                    applyFlowChange={() => null}
                    onClose={onClose}
                    setNodeErrors={() => null}
                    approval={approval}
                />
            </div>
            <Paper
                sx={{
                    height: '85%',
                    minWidth: '25%',
                    maxWidth: '25%',
                    borderRadius: '0px 12px 12px 0px',
                    borderLeft: 'none',
                    padding: 2
                }}>
                {statusAuditData === 'success'
                    ? (
                            <PanelSection
                                title='Outputs'
                                mode={showModeOutputs}
                                setMode={setShowModeOutputs}
                                data={outputs}
                                error={outputError}
                                errorMessage={auditData?.configs?.memoryData?.memory?.errorMessage}
                                renderContent={(output, i) => <AuditAccordion key={i + 1} data={output} showMode={showModeOutputs} nodeId={selectedNode.uuid} eeId={execEnv.uuid} inOut='output' nodeType={selectedNode.type} artifacts={execEnv?.artifacts} />}
                            />
                        )
                    : <CircularProgress />}
            </Paper>
        </>
    )
}

export default NodeAuditPanel
